import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CalendarTask {
  id: number;
  title: string;
  description: string | null;
  category: string;
  dueDate: string | null;
  status: string;
  priority: string;
  sprintName: string | null;
  sortOrder: number;
  notes: string | null;
  createdBy: string;
  conversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

type StatusFilter = 'all' | 'active' | 'completed' | 'overdue';

// ─── Constants ──────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  creative: { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-700 dark:text-purple-300' },
  production: { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-700 dark:text-amber-300' },
  marketing: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-300' },
  admin: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400' },
  general: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400' },
};

const PRIORITY_DOTS: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-blue-400',
  low: 'bg-gray-300 dark:bg-gray-600',
};

const CATEGORIES = ['general', 'creative', 'production', 'marketing', 'admin'];

// ─── Main Component ─────────────────────────────────────────────────────────

export function CMSTasks() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [tasks, setTasks] = useState<CalendarTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [modalTask, setModalTask] = useState<CalendarTask | null>(null);
  const [editingTask, setEditingTask] = useState<Partial<CalendarTask> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Quick add state
  const [quickTitle, setQuickTitle] = useState('');
  const [quickDueDate, setQuickDueDate] = useState(todayStr());
  const [quickCategory, setQuickCategory] = useState('general');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (token) loadTasks();
  }, [token, statusFilter, categoryFilter]);

  async function loadTasks() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter !== 'all') params.set('category', categoryFilter);

      params.set('action', 'calendar-list');
      const res = await fetch(`/api/cms/ai?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load tasks');
      const data = await res.json();
      // Normalize ISO timestamps to YYYY-MM-DD
      const normalized = (data.tasks || []).map((t: CalendarTask) => ({
        ...t,
        dueDate: toDateOnly(t.dueDate),
      }));
      setTasks(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleStatus(task: CalendarTask) {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';

    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t))
    );

    try {
      const res = await fetch('/api/cms/ai?action=calendar-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: task.id, status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to update task');
    } catch {
      // Revert on error
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: task.status } : t))
      );
      setError('Failed to update task status');
    }
  }

  async function handleQuickAdd() {
    if (!quickTitle.trim() || adding) return;
    setAdding(true);
    setError(null);

    try {
      const res = await fetch('/api/cms/ai?action=calendar-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: quickTitle.trim(),
          due_date: quickDueDate || null,
          category: quickCategory,
          created_by: 'user',
        }),
      });
      if (!res.ok) throw new Error('Failed to create task');
      setQuickTitle('');
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setAdding(false);
    }
  }

  async function handleSaveEdit() {
    if (!editingTask?.id) return;

    try {
      const res = await fetch('/api/cms/ai?action=calendar-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: editingTask.id,
          title: editingTask.title,
          description: editingTask.description,
          due_date: editingTask.dueDate,
          category: editingTask.category,
          priority: editingTask.priority,
          notes: editingTask.notes,
        }),
      });
      if (!res.ok) throw new Error('Failed to update task');
      setEditingTask(null);
      setModalTask(null);
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  }

  async function handleDelete(taskId: number) {
    if (!confirm('Delete this task?')) return;

    try {
      const res = await fetch('/api/cms/ai?action=calendar-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: taskId }),
      });
      if (!res.ok) throw new Error('Failed to delete task');
      setModalTask(null);
      setEditingTask(null);
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  }

  // ─── Grouping ─────────────────────────────────────────────────────────────

  const grouped = groupTasksByDate(tasks);

  // ─── Summary ──────────────────────────────────────────────────────────────

  const today = todayStr();
  const week = weekFromNow();
  const todayTasks = tasks.filter(
    (t) => toDateOnly(t.dueDate) === today && t.status !== 'skipped'
  ).sort((a, b) => {
    // Completed at the bottom, then by sort_order
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });
  const todayPending = todayTasks.filter((t) => t.status !== 'completed').length;
  const todayDone = todayTasks.filter((t) => t.status === 'completed').length;
  const overdueCount = tasks.filter(
    (t) => { const d = toDateOnly(t.dueDate); return d && d < today && t.status !== 'completed' && t.status !== 'skipped'; }
  ).length;
  const weekCount = tasks.filter(
    (t) => { const d = toDateOnly(t.dueDate); return t.status !== 'completed' && t.status !== 'skipped' && d && d >= today && d <= week; }
  ).length;
  const todaySprint = todayTasks.find((t) => t.sprintName)?.sprintName || null;

  return (
    <div className="min-h-screen bg-brand-sand dark:bg-brand-dark">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm dark:bg-brand-charcoal/80 dark:shadow-none dark:border-b dark:border-white/10">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/cms/dashboard')}
              className="text-sm text-brand-gray hover:text-brand-dark dark:text-white/60 dark:hover:text-white transition-colors"
            >
              ← Dashboard
            </button>
            <div className="h-4 w-px bg-black/10 dark:bg-white/10" />
            <h1 className="text-xl font-bold text-brand-dark dark:text-white">Task Board</h1>
          </div>
          <button
            onClick={() => navigate('/cms/ai-assistant')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Ask AI to plan
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Today's Focus */}
        {!loading && todayTasks.length > 0 && (
          <div className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl border border-brand-blue/20 dark:border-brand-blue/15 overflow-hidden">
            <div className="px-4 py-3 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {todayPending === 0 ? '🎉' : '📋'}
                </span>
                <div>
                  <h2 className="text-sm font-bold text-brand-dark dark:text-white">
                    Today's Focus
                  </h2>
                  <p className="text-[11px] text-brand-gray dark:text-white/50">
                    {todaySprint && <span className="text-rose-600 dark:text-rose-400 font-medium">{todaySprint} — </span>}
                    {todayDone}/{todayTasks.length} done
                    {overdueCount > 0 && <span className="text-red-500 ml-2">+ {overdueCount} overdue</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-brand-gray dark:text-white/50">
                <span>{weekCount} this week</span>
              </div>
            </div>
            <div className="divide-y divide-black/[0.03] dark:divide-white/[0.03]">
              {todayTasks.map((task) => {
                const isCompleted = task.status === 'completed';
                const catColor = CATEGORY_COLORS[task.category] || CATEGORY_COLORS.general;
                const priorityDot = PRIORITY_DOTS[task.priority] || PRIORITY_DOTS.medium;
                return (
                  <div key={task.id} className={`flex items-start gap-3 px-4 py-2.5 ${isCompleted ? 'opacity-50' : ''}`}>
                    <button
                      onClick={() => handleToggleStatus(task)}
                      className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-md border-2 transition-colors flex items-center justify-center ${
                        isCompleted
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-black/20 dark:border-white/20 hover:border-brand-blue'
                      }`}
                    >
                      {isCompleted && (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priorityDot}`} />
                        <span className={`text-sm font-medium ${isCompleted ? 'line-through text-brand-gray dark:text-white/40' : 'text-brand-dark dark:text-white'}`}>
                          {task.title}
                        </span>
                        <span className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${catColor.bg} ${catColor.text}`}>
                          {task.category}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-xs text-brand-gray dark:text-white/50 mt-1 whitespace-pre-line leading-relaxed">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary Strip (no today tasks) */}
        {!loading && todayTasks.length === 0 && (
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-brand-dark dark:text-white font-medium">
              No tasks today
            </span>
            {overdueCount > 0 && (
              <span className="text-red-600 dark:text-red-400 font-medium">
                {overdueCount} overdue
              </span>
            )}
            <span className="text-brand-gray dark:text-white/60">
              {weekCount} this week
            </span>
          </div>
        )}

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-black/10 dark:border-white/10 overflow-hidden">
            {(['all', 'active', 'completed', 'overdue'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors capitalize ${
                  statusFilter === s
                    ? 'bg-brand-blue text-white'
                    : 'text-brand-gray dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 text-xs border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-charcoal text-brand-dark dark:text-white"
          >
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Quick Add */}
        <div className="flex gap-2 items-end bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl border border-black/5 dark:border-white/10 p-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Add a task..."
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd(); }}
              className="w-full px-3 py-2 text-sm border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white placeholder-black/30 dark:placeholder-white/25 focus:outline-none focus:border-brand-blue/50"
            />
          </div>
          <DateInput
            value={quickDueDate}
            onChange={setQuickDueDate}
          />
          <select
            value={quickCategory}
            onChange={(e) => setQuickCategory(e.target.value)}
            className="px-2 py-2 text-xs border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white hidden md:block"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
          <button
            onClick={handleQuickAdd}
            disabled={!quickTitle.trim() || adding}
            className="px-4 py-2 text-sm font-medium bg-brand-blue hover:bg-brand-dark text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {adding ? '...' : 'Add'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline text-xs">dismiss</button>
          </div>
        )}

        {/* Task List */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-brand-gray dark:text-white/50 text-sm">
            <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading tasks...
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-16 text-brand-gray dark:text-white/40">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm font-medium">No tasks yet</p>
            <p className="text-xs mt-1">Add one above or ask the AI to plan your week</p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map((group) => (
              <div key={group.label}>
                {/* Day Header */}
                <div className={`flex items-center gap-2 mb-2 ${group.isOverdue ? 'text-red-600 dark:text-red-400' : 'text-brand-dark dark:text-white'}`}>
                  <h2 className="text-sm font-semibold">{group.label}</h2>
                  <span className="text-xs text-brand-gray dark:text-white/40">({group.tasks.length})</span>
                </div>

                {/* Sprint sub-headers */}
                {groupBySprint(group.tasks).map((sprintGroup) => (
                  <div key={sprintGroup.sprint || '__none'} className="mb-3">
                    {sprintGroup.sprint && (
                      <div className="flex items-center gap-2 mb-1.5 ml-7">
                        <svg className="w-3 h-3 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className="text-xs font-medium text-rose-600 dark:text-rose-400">{sprintGroup.sprint}</span>
                      </div>
                    )}

                    <div className="space-y-1">
                      {sprintGroup.tasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onToggle={() => handleToggleStatus(task)}
                          onOpen={() => setModalTask(task)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Task Detail Modal */}
      {modalTask && (
        <TaskDetailModal
          task={modalTask}
          isEditing={editingTask?.id === modalTask.id}
          editingTask={editingTask?.id === modalTask.id ? editingTask : null}
          onClose={() => { setModalTask(null); setEditingTask(null); }}
          onToggle={() => handleToggleStatus(modalTask)}
          onStartEdit={() => setEditingTask({ ...modalTask, dueDate: toDateOnly(modalTask.dueDate) })}
          onEditChange={(changes) => setEditingTask((prev) => prev ? { ...prev, ...changes } : null)}
          onSave={handleSaveEdit}
          onDelete={() => handleDelete(modalTask.id)}
          onCancelEdit={() => setEditingTask(null)}
        />
      )}
    </div>
  );
}

// ─── Task Card ──────────────────────────────────────────────────────────────

function TaskCard({
  task,
  onToggle,
  onOpen,
}: {
  task: CalendarTask;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const isCompleted = task.status === 'completed';
  const catColor = CATEGORY_COLORS[task.category] || CATEGORY_COLORS.general;
  const priorityDot = PRIORITY_DOTS[task.priority] || PRIORITY_DOTS.medium;

  return (
    <div className={`rounded-xl border transition-all ${
      isCompleted
        ? 'border-black/5 dark:border-white/5 opacity-60'
        : 'border-black/10 dark:border-white/10'
    } bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md`}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        {/* Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className={`flex-shrink-0 w-5 h-5 rounded-md border-2 transition-colors flex items-center justify-center ${
            isCompleted
              ? 'bg-green-500 border-green-500 text-white'
              : 'border-black/20 dark:border-white/20 hover:border-brand-blue'
          }`}
        >
          {isCompleted && (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        {/* Title + Description — click opens modal */}
        <button
          onClick={onOpen}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priorityDot}`} />
            <span className={`text-sm font-medium truncate ${
              isCompleted ? 'line-through text-brand-gray dark:text-white/40' : 'text-brand-dark dark:text-white'
            }`}>
              {task.title}
            </span>
          </div>
          {task.description && (
            <p className="text-xs text-brand-gray dark:text-white/40 line-clamp-2 mt-0.5 ml-3.5">
              {task.description}
            </p>
          )}
        </button>

        {/* Category Badge */}
        <span className={`flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full ${catColor.bg} ${catColor.text}`}>
          {task.category}
        </span>

        {/* AI Badge */}
        {task.createdBy === 'ai' && (
          <span className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400">
            AI
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Task Detail Modal ──────────────────────────────────────────────────────

const PRIORITY_LABELS: Record<string, { label: string; color: string }> = {
  urgent: { label: 'Urgent', color: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' },
  high: { label: 'High', color: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300' },
  medium: { label: 'Medium', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' },
  low: { label: 'Low', color: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  todo: { label: 'To Do', color: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' },
  completed: { label: 'Completed', color: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' },
  skipped: { label: 'Skipped', color: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500' },
};

function TaskDetailModal({
  task,
  isEditing,
  editingTask,
  onClose,
  onToggle,
  onStartEdit,
  onEditChange,
  onSave,
  onDelete,
  onCancelEdit,
}: {
  task: CalendarTask;
  isEditing: boolean;
  editingTask: Partial<CalendarTask> | null;
  onClose: () => void;
  onToggle: () => void;
  onStartEdit: () => void;
  onEditChange: (changes: Partial<CalendarTask>) => void;
  onSave: () => void;
  onDelete: () => void;
  onCancelEdit: () => void;
}) {
  const catColor = CATEGORY_COLORS[task.category] || CATEGORY_COLORS.general;
  const priorityInfo = PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium;
  const statusInfo = STATUS_LABELS[task.status] || STATUS_LABELS.todo;
  const isCompleted = task.status === 'completed';

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-auto bg-white dark:bg-brand-charcoal rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-3 border-b border-black/5 dark:border-white/5">
          <button
            onClick={onToggle}
            className={`flex-shrink-0 w-6 h-6 mt-0.5 rounded-lg border-2 transition-colors flex items-center justify-center ${
              isCompleted
                ? 'bg-green-500 border-green-500 text-white'
                : 'border-black/20 dark:border-white/20 hover:border-brand-blue'
            }`}
          >
            {isCompleted && (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          <div className="flex-1 min-w-0">
            {isEditing && editingTask ? (
              <input
                type="text"
                value={editingTask.title || ''}
                onChange={(e) => onEditChange({ title: e.target.value })}
                className="w-full text-lg font-bold px-2 py-1 border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white focus:outline-none focus:border-brand-blue/50"
              />
            ) : (
              <h2 className={`text-lg font-bold ${isCompleted ? 'line-through text-brand-gray dark:text-white/40' : 'text-brand-dark dark:text-white'}`}>
                {task.title}
              </h2>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${priorityInfo.color}`}>
                {priorityInfo.label}
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${catColor.bg} ${catColor.text}`}>
                {task.category}
              </span>
              {task.createdBy === 'ai' && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400">
                  AI
                </span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="flex-shrink-0 p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          >
            <svg className="w-5 h-5 text-brand-gray dark:text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Meta row */}
          {isEditing && editingTask ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-brand-gray dark:text-white/50 mb-1 uppercase tracking-wider">Due Date</label>
                <DateInput
                  value={toDateOnly(editingTask.dueDate) || ''}
                  onChange={(val) => onEditChange({ dueDate: val })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-brand-gray dark:text-white/50 mb-1 uppercase tracking-wider">Category</label>
                <select
                  value={editingTask.category || 'general'}
                  onChange={(e) => onEditChange({ category: e.target.value })}
                  className="w-full px-2 py-2 text-xs border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-brand-gray dark:text-white/50 mb-1 uppercase tracking-wider">Priority</label>
                <select
                  value={editingTask.priority || 'medium'}
                  onChange={(e) => onEditChange({ priority: e.target.value })}
                  className="w-full px-2 py-2 text-xs border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-brand-gray dark:text-white/50">
              {task.dueDate && (
                <span>Due: <span className="text-brand-dark dark:text-white font-medium">{formatDateDisplay(toDateOnly(task.dueDate) || '')}</span></span>
              )}
              {task.sprintName && (
                <span>Sprint: <span className="text-rose-600 dark:text-rose-400 font-medium">{task.sprintName}</span></span>
              )}
              <span>Created: {new Date(task.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-[10px] font-medium text-brand-gray dark:text-white/50 mb-1.5 uppercase tracking-wider">Description</label>
            {isEditing && editingTask ? (
              <textarea
                value={editingTask.description || ''}
                onChange={(e) => onEditChange({ description: e.target.value })}
                rows={5}
                className="w-full px-3 py-2 text-sm border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white focus:outline-none focus:border-brand-blue/50 resize-y"
                placeholder="Add a description..."
              />
            ) : task.description ? (
              <p className="text-sm text-brand-dark dark:text-white/80 whitespace-pre-line leading-relaxed">
                {task.description}
              </p>
            ) : (
              <p className="text-sm text-brand-gray dark:text-white/30 italic">No description</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] font-medium text-brand-gray dark:text-white/50 mb-1.5 uppercase tracking-wider">Notes</label>
            {isEditing && editingTask ? (
              <textarea
                value={editingTask.notes || ''}
                onChange={(e) => onEditChange({ notes: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white focus:outline-none focus:border-brand-blue/50 resize-y"
                placeholder="Add notes..."
              />
            ) : task.notes ? (
              <p className="text-sm text-brand-dark dark:text-white/80 whitespace-pre-line leading-relaxed">
                {task.notes}
              </p>
            ) : (
              <p className="text-sm text-brand-gray dark:text-white/30 italic">No notes</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-black/5 dark:border-white/5 bg-white/50 dark:bg-brand-dark/30">
          {isEditing ? (
            <>
              <button
                onClick={onSave}
                className="px-4 py-2 text-sm font-medium bg-brand-blue hover:bg-brand-dark text-white rounded-lg transition-colors"
              >
                Save
              </button>
              <button
                onClick={onCancelEdit}
                className="px-4 py-2 text-sm font-medium border border-black/10 dark:border-white/10 text-brand-gray dark:text-white/60 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={onStartEdit}
              className="px-4 py-2 text-sm font-medium bg-brand-blue hover:bg-brand-dark text-white rounded-lg transition-colors"
            >
              Edit
            </button>
          )}
          <button
            onClick={onDelete}
            className="ml-auto px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Date Input (DD/MM/YYYY) ────────────────────────────────────────────────

function formatDateDisplay(isoDate: string): string {
  if (!isoDate) return '';
  const clean = toDateOnly(isoDate) || isoDate;
  const [y, m, d] = clean.split('-');
  return `${d}/${m}/${y}`;
}

function DateInput({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (val: string) => void;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={`relative ${className || ''}`}>
      {/* Visible formatted label */}
      <div
        onClick={() => inputRef.current?.showPicker()}
        className="px-2 py-2 text-xs border border-black/10 dark:border-white/10 rounded-lg bg-white dark:bg-brand-dark text-brand-dark dark:text-white cursor-pointer flex items-center justify-between gap-1"
      >
        <span>{value ? formatDateDisplay(value) : 'No date'}</span>
        <svg className="w-3.5 h-3.5 text-brand-gray dark:text-white/40 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      {/* Hidden native date input — positioned on top so showPicker works */}
      <input
        ref={inputRef}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Normalize any date value to YYYY-MM-DD (handles ISO timestamps, Date objects, locale strings) */
function toDateOnly(val: any): string | null {
  if (!val) return null;
  const s = String(val);
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO timestamp like "2026-02-20T23:00:00.000Z"
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  // Anything else (locale string, Date.toString()) — parse and re-format
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }
  return null;
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function weekFromNow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
}

function formatDateLabel(dateStr: string): { label: string; isOverdue: boolean } {
  // Normalize in case raw ISO timestamp leaks through
  const clean = toDateOnly(dateStr) || dateStr;
  const today = todayStr();
  const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();

  const d = new Date(clean + 'T12:00:00');
  if (isNaN(d.getTime())) {
    return { label: clean, isOverdue: clean < today };
  }
  const formatted = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

  if (clean < today) {
    return { label: `Overdue — ${formatted}`, isOverdue: true };
  }
  if (clean === today) {
    return { label: `Today — ${formatted}`, isOverdue: false };
  }
  if (clean === tomorrow) {
    return { label: `Tomorrow — ${formatted}`, isOverdue: false };
  }
  const longFormatted = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
  return { label: longFormatted, isOverdue: false };
}

interface DateGroup {
  label: string;
  isOverdue: boolean;
  tasks: CalendarTask[];
}

function groupTasksByDate(tasks: CalendarTask[]): DateGroup[] {
  const groups = new Map<string, CalendarTask[]>();
  const noDate: CalendarTask[] = [];

  for (const task of tasks) {
    const normalized = toDateOnly(task.dueDate);
    if (!normalized) {
      noDate.push(task);
      continue;
    }
    const key = normalized;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(task);
  }

  const result: DateGroup[] = [];

  // Sort date keys
  const sortedKeys = [...groups.keys()].sort();
  for (const key of sortedKeys) {
    const { label, isOverdue } = formatDateLabel(key);
    result.push({ label, isOverdue, tasks: groups.get(key)! });
  }

  if (noDate.length > 0) {
    result.push({ label: 'No date', isOverdue: false, tasks: noDate });
  }

  return result;
}

function groupBySprint(tasks: CalendarTask[]): Array<{ sprint: string | null; tasks: CalendarTask[] }> {
  const sprints = new Map<string | null, CalendarTask[]>();

  for (const task of tasks) {
    const key = task.sprintName || null;
    if (!sprints.has(key)) sprints.set(key, []);
    sprints.get(key)!.push(task);
  }

  // Non-sprint tasks first, then sprint groups
  const result: Array<{ sprint: string | null; tasks: CalendarTask[] }> = [];
  if (sprints.has(null)) {
    result.push({ sprint: null, tasks: sprints.get(null)! });
  }
  for (const [sprint, sprintTasks] of sprints) {
    if (sprint !== null) {
      result.push({ sprint, tasks: sprintTasks });
    }
  }

  return result;
}
